import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import ApiService from '../../services/ApiService';
import {
  Container,
  PriceInputContainer,
  InputDiv,
  Label,
  Input,
  Select,
  PriceListContainer,
  PriceItemContainer,
  AddButton,
  DeleteButton
} from '../PriceManager/style';

export interface CostItem {
  cost: number;
  unitOfMeasurementId: number;
}

export interface UnitOfMeasurement {
  id: number;
  name: string;
}

export interface CostManagerRef {
  addPendingCost: () => boolean;
}

interface CostManagerProps {
  costs: CostItem[];
  onChange: (costs: CostItem[]) => void;
  label?: string;
  /** Unidades já persistidas: linhas em leitura (o embed da venda é append-only). */
  lockedUnitIds?: number[];
}

const CostManager = forwardRef<CostManagerRef, CostManagerProps>(({
  costs,
  onChange,
  label = "Custos",
  lockedUnitIds
}, ref) => {
  const lockedIds = lockedUnitIds || [];
  const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
  const [newCost, setNewCost] = useState<{ cost: string; unitOfMeasurementId: string }>({
    cost: '',
    unitOfMeasurementId: ''
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const startEditing = (index: number, currentCost: number) => {
    setEditingIndex(index);
    setEditingValue(formatCurrency(String(currentCost * 100)));
  };

  const saveEdit = (index: number) => {
    const updatedCostValue = parseCurrency(editingValue);
    const updatedCosts = costs.map((cost, i) => i === index ? { ...cost, cost: updatedCostValue } : cost);
    onChange(updatedCosts);
    setEditingIndex(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');

    if (!numericValue) return '';

    const numberValue = parseInt(numericValue) / 100;

    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseCurrency = (value: string) => {
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const handleCostChange = (value: string) => {
    const formattedValue = formatCurrency(value);
    setNewCost({ ...newCost, cost: formattedValue });
  };

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await ApiService.services.getUnits();
        setUnits(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar unidades de medida do custo:', error);
      }
    };

    fetchUnits();
  }, []);

  const addCost = () => {
    if (newCost.cost && newCost.unitOfMeasurementId) {
      const unitExists = costs.some(cost =>
        cost.unitOfMeasurementId === parseInt(newCost.unitOfMeasurementId)
      );

      if (unitExists) {
        alert('Esta unidade de medida já foi adicionada. Escolha uma unidade diferente.');
        return false;
      }

      onChange([...costs, {
        cost: parseCurrency(newCost.cost),
        unitOfMeasurementId: parseInt(newCost.unitOfMeasurementId)
      }]);
      setNewCost({ cost: '', unitOfMeasurementId: '' });
      return true;
    }
    return false;
  };

  const addPendingCost = () => {
    if (newCost.cost && newCost.unitOfMeasurementId) {
      const unitExists = costs.some(cost =>
        cost.unitOfMeasurementId === parseInt(newCost.unitOfMeasurementId)
      );

      if (unitExists) {
        return false;
      }

      const newCostItem = {
        cost: parseCurrency(newCost.cost),
        unitOfMeasurementId: parseInt(newCost.unitOfMeasurementId)
      };

      onChange([...costs, newCostItem]);
      setNewCost({ cost: '', unitOfMeasurementId: '' });

      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    addPendingCost
  }));

  const removeCost = (index: number) => {
    const updatedCosts = costs.filter((_, i) => i !== index);
    onChange(updatedCosts);
  };

  const availableUnits = units.filter(unit =>
    !costs.some(cost => cost.unitOfMeasurementId === unit.id)
  );

  const getUnitName = (unitId: number) => {
    return units.find(unit => unit.id === unitId)?.name || '';
  };

  const formatCost = (cost: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cost);
  };

  return (
    <Container>
      <PriceInputContainer>
        <InputDiv>
          <Label>Custo (R$)</Label>
          <Input
            type="text"
            value={newCost.cost}
            onChange={(e) => handleCostChange(e.target.value)}
            placeholder="0,00"
          />
        </InputDiv>

        <InputDiv>
          <Label>Unidade</Label>
          <Select
            value={newCost.unitOfMeasurementId}
            onChange={(e) => setNewCost({ ...newCost, unitOfMeasurementId: e.target.value })}
          >
            <option value=""></option>
            {availableUnits.map(unit => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </Select>
        </InputDiv>
      </PriceInputContainer>

      <AddButton
        onClick={addCost}
        disabled={!newCost.cost || !newCost.unitOfMeasurementId}
      >
        <Plus size={16} />
        {costs.length === 0 ? `Adicionar ${label.toLowerCase()}` : 'Adicionar outro custo'}
      </AddButton>

      {costs.length > 0 && (
        <PriceListContainer>
          {costs.map((cost, index) => (
            <PriceItemContainer key={index}>
              {editingIndex === index ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <Input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(formatCurrency(e.target.value))}
                    style={{ flex: 1, padding: '0 8px', height: '30px', borderRadius: '4px' }}
                  />
                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                    / {getUnitName(cost.unitOfMeasurementId)}
                  </span>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                    <button
                      onClick={() => saveEdit(index)}
                      disabled={!editingValue}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center' }}
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={cancelEdit}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span>
                    {formatCost(cost.cost)} / {getUnitName(cost.unitOfMeasurementId)}
                  </span>
                  {!lockedIds.includes(cost.unitOfMeasurementId) && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => startEditing(index, cost.cost)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', display: 'flex', alignItems: 'center', padding: 0 }}
                      >
                        <Pencil size={12} />
                      </button>
                      <DeleteButton onClick={() => removeCost(index)} style={{ margin: 0 }}>
                        <Trash2 size={12} />
                      </DeleteButton>
                    </div>
                  )}
                </>
              )}
            </PriceItemContainer>
          ))}
        </PriceListContainer>
      )}
    </Container>
  );
});

CostManager.displayName = 'CostManager';

export default CostManager;
